import { AsyncPipe } from '@angular/common';
import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { VehicleType } from '@polaris-customerweb/sharedcomponents.core';
import { NotificationMessageService } from '@polaris-customerweb/sharedcomponents.garage';
import { LogService } from '@polaris-customerweb/sharedcomponents.shared';
import { attempt, kebabCase } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ConfirmDialogOptions } from 'src/app/components/shared/components/confirm-dialog/confirm-dialog-options';
import { ConfirmDialogComponent } from 'src/app/components/shared/components/confirm-dialog/confirm-dialog.component';
import { CommunityItem } from 'src/app/models/community-item';
import { Share, ShareItem } from 'src/app/models/share';
import {
    BranchInitData,
    BranchPayloadContactMessage,
    BranchPayloadContactOptConfirmation,
    BranchPayloadNotificationMessage,
    BranchPayloadRideGroup,
    BranchPayloadShare,
    BranchService,
    payloadIsContactMessage,
    payloadIsContactOptConfirmation,
    payloadIsNotificationMessage,
    payloadIsRideGroup,
    payloadIsShare,
} from 'src/app/services/branch.service';
import { CommunityItemService } from 'src/app/services/community-item.service';
import { NotificationNavigationService } from 'src/app/services/notification-navigation.service';
import { ShareService } from 'src/app/services/share.service';
import { WINDOW } from 'src/app/state/app-config/factories/window.factory';
import { UserAuthService } from 'src/app/state/user-auth/user-auth.service';
import { HomeAuthenticatedComponent } from './home-authenticated/home-authenticated.component';

const SHARE_NOT_AVAILABLE = 'Share not available';
const FEATURE_NOT_AVAILABLE = 'Feature not available';
const NO_BRANCH_DATA = 'No branch data';

@Component({
    imports: [AsyncPipe, HomeAuthenticatedComponent],
    selector: 'rdx-home',
    templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
    readonly className = 'HomeComponent';
    vehicleType: VehicleType = 'Unknown';
    isAuthenticated$: Observable<boolean>;
    isDestroyed = false;

    private destructors: (() => void)[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private configService: ConfigService,
        private branchService: BranchService,
        private communityItemService: CommunityItemService,
        private logService: LogService,
        private matDialog: MatDialog,
        private ngZone: NgZone,
        private notificationNavigationService: NotificationNavigationService,
        private notificationMessageService: NotificationMessageService,
        private router: Router,
        private shareService: ShareService,
        private userAuthService: UserAuthService,
        @Inject(WINDOW) private window: Window
    ) {
        this.isAuthenticated$ = this.userAuthService.isAuthenticated$;
    }

    ngOnInit(): void {
        this.vehicleType = this.configService.appSku;
        this.branchService.init({}, (err, data) => {
            const zoneSubscription = this.ngZone.run(() =>
                this.getShareFromBranchData(err, data)
                    .pipe(
                        switchMap((item) => {
                            if (payloadIsContactMessage(item)) {
                                return this.navigateToContactMessage(
                                    item.payload.messageId
                                );
                            } else if (payloadIsNotificationMessage(item)) {
                                return of(
                                    this.notificationNavigationService.navigateToNotification(
                                        item.payload
                                    )
                                );
                            } else if (payloadIsContactOptConfirmation(item)) {
                                switch (item.type) {
                                    case 'contactConfirmation':
                                        return this.navigateToContactConfirmation(
                                            item.payload.contactId,
                                            item.payload
                                        );
                                    case 'contactOptOut':
                                        return this.navigateToContactOptOut(
                                            item.payload.contactId,
                                            item.payload
                                        );
                                }
                            } else if (payloadIsRideGroup(item)) {
                                return this.navigateToRideGroup(
                                    item.payload.inviteCode
                                );
                            } else if (payloadIsShare(item)) {
                                return this.navigateToShare(item.payload);
                            } else {
                                return throwError(
                                    () => new Error('Unknown branch type')
                                );
                            }
                        }),
                        catchError((error) => {
                            if (
                                error.message === SHARE_NOT_AVAILABLE ||
                                // original content has been deleted
                                error.status === 410 ||
                                // item has been unshared
                                error.status === 403
                            ) {
                                this.showShareNotAvailableDialog();
                            } else if (
                                error.message === FEATURE_NOT_AVAILABLE
                            ) {
                                this.showFeatureNotAvailableDialog();
                            } else if (error.message !== NO_BRANCH_DATA) {
                                this.logService.error(this.className, {
                                    branchId:
                                        this.activatedRoute.snapshot.queryParamMap.get(
                                            '_branch_match_id'
                                        ),
                                    errorMessage: error.message,
                                });
                            }

                            return of(false);
                        })
                    )
                    .subscribe({
                        error: (error) => {
                            this.logService.error(this.className, error);
                        },
                        next: (isNavigating) => {
                            if (!isNavigating) {
                                this.checkPasswordChange();
                            }
                        },
                    })
            );
            this.destructors.push(() => {
                zoneSubscription.unsubscribe();
            });
        });
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.destructors.forEach(attempt);
    }

    private checkPasswordChange(): void {
        this.activatedRoute.paramMap.pipe(take(1)).subscribe((params) => {
            if (params.get('email')) {
                if (params.get('success') === 'true') {
                    this.showPasswordChangeSuccessful();
                } else {
                    this.showPasswordChangeNotSuccessful();
                }
            }
        });
    }

    private showPasswordChangeSuccessful(): void {
        const data = new ConfirmDialogOptions({
            confirmButtonId: 'passwordChangeSuccessfulConfirm',
            confirmText: $localize`OK`,
            contentText: $localize`Password change succeeded, log in using new password.`,
            dialogId: 'passwordChangeSuccessful',
            hideCancelButton: true,
            titleText: $localize`Password Change`,
        });
        this.matDialog.open(ConfirmDialogComponent, { data });
    }

    private showPasswordChangeNotSuccessful(): void {
        const data = new ConfirmDialogOptions({
            confirmButtonId: 'passwordChangeNotSuccessfulConfirm',
            confirmText: $localize`OK`,
            contentText: $localize`Password change was not successful, please try again.`,
            dialogId: 'passwordChangeNotSuccessful',
            hideCancelButton: true,
            titleText: $localize`Password Change`,
        });
        this.matDialog.open(ConfirmDialogComponent, { data });
    }

    private showShareNotAvailableDialog(): void {
        void this.router.navigate(['share-unavailable']);
    }

    private showFeatureNotAvailableDialog(): void {
        const data = new ConfirmDialogOptions({
            confirmButtonId: 'featureNotAvailableConfirm',
            confirmText: $localize`OK`,
            contentText: $localize`The content you are trying to access is not available on the web. Please download the RIDE COMMAND® app.`,
            dialogId: 'featureNotAvailable',
            hideAppStoreButtons: false,
            hideCancelButton: true,
            titleText: $localize`Feature Unavailable`,
        });
        this.matDialog.open(ConfirmDialogComponent, { data });
    }

    private getShareFromBranchData(
        err: string,
        data: BranchInitData
    ): Observable<
        | BranchPayloadContactMessage
        | BranchPayloadNotificationMessage
        | BranchPayloadContactOptConfirmation
        | BranchPayloadShare
        | BranchPayloadRideGroup
    > {
        if (err) {
            return throwError(
                () => new Error('Error initializing the Branch SDK.')
            );
        }

        if (!data.data) {
            return throwError(() => new Error(NO_BRANCH_DATA));
        }

        // is a contact opt out
        if (data.data_parsed.NavigateTo === 'ContactNotificationPreferences') {
            const {
                contactEmail,
                contactId = '',
                contactPhone,
                riderFirstName,
                riderId,
                riderLastName,
            } = data.data_parsed;

            return of({
                payload: {
                    contactEmail,
                    contactId,
                    contactPhone,
                    riderFirstName,
                    riderId,
                    riderLastName,
                },
                type: 'contactOptOut',
            });
        }

        // is a contact confirmation
        if (
            data.data_parsed.notificationType === 'ContactConfirmationMessage'
        ) {
            const {
                contactId = '',
                contactNotificationType,
                riderFirstName,
                riderId,
                riderLastName,
                vehicleColor,
                vehicleId,
                vehicleModel,
                vehicleName,
                vehicleYear,
            } = data.data_parsed;
            return of({
                payload: {
                    contactId,
                    contactNotificationType,
                    riderFirstName,
                    riderId,
                    riderLastName,
                    vehicleColor,
                    vehicleId,
                    vehicleModel,
                    vehicleName,
                    vehicleYear,
                },
                type: 'contactConfirmation',
            });
        }

        // is a check-in or help message
        if (
            data.data_parsed.notificationType === 'CheckInMessage' ||
            data.data_parsed.notificationType === 'HelpMessage'
        ) {
            const { messageId = '' } = data.data_parsed;
            return of({
                payload: {
                    messageId,
                },
                type: 'contactMessage',
            });
        }

        const notificationId = data.data_parsed.notificationId;
        if (notificationId) {
            return toObservable(
                this.notificationMessageService.$notifications
            ).pipe(
                map(
                    (notifications) =>
                        ({
                            payload: notifications.find(
                                (item) =>
                                    item.properties.notificationId ===
                                    notificationId
                            ),
                            type: 'notification',
                        }) as BranchPayloadNotificationMessage
                ),
                catchError((error) => {
                    if (error.message === 'Login required') {
                        this.userAuthService.promptAuthenticationAndNavigate(
                            `${this.window.location.pathname}${this.window.location.search}`
                        );
                    }
                    return throwError(() => error);
                })
            );
        }

        const shareId: string | undefined = data.data_parsed.shareId;
        if (shareId) {
            return this.shareService.getFullShare(shareId).pipe(
                map((share) => ({
                    payload: share,
                    type: 'share',
                }))
            );
        }

        const inviteCode = data.data_parsed.invitecode;
        if (inviteCode) {
            return of({ payload: { inviteCode }, type: 'rideGroup' });
        }

        return throwError(() => new Error(NO_BRANCH_DATA));
    }

    private navigateToContactConfirmation(
        contactId: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any
    ): Observable<boolean> {
        void this.router.navigate(
            ['check-in-contacts', 'confirmation', contactId],
            {
                queryParams: data,
            }
        );

        return of(true);
    }

    private navigateToContactOptOut(
        contactId: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any
    ): Observable<boolean> {
        void this.router.navigate(
            ['check-in-contacts', 'stop-messages', contactId],
            {
                queryParams: {
                    email: data.contactEmail,
                    firstName: data.riderFirstName,
                    lastName: data.riderLastName,
                    phone: data.contactPhone,
                    riderId: data.riderId,
                },
            }
        );

        return of(true);
    }

    private navigateToContactMessage(messageId: string): Observable<boolean> {
        void this.router.navigate(['check-in-contacts', 'messages', messageId]);
        return of(true);
    }

    private navigateToShare(share?: Share): Observable<boolean> {
        if (!share) {
            return throwError(() => new Error('Invalid share returned'));
        }

        const shareId = share.id;
        if (!share.isActive) {
            return throwError(() => new Error(SHARE_NOT_AVAILABLE));
        }

        const firstItem = share.sharedItems[0] as ShareItem | undefined;
        if (!firstItem) {
            return throwError(
                () => new Error('The share contained no valid items.')
            );
        }

        if (firstItem.entityType === 'CommunityItem') {
            return this.communityItemService
                .getFullCommunityItem(firstItem.id)
                .pipe(
                    map((item) => {
                        this.navigateToCommunityItem(item);
                        return true;
                    })
                );
        } else if (
            ![
                'PlannedRide',
                'TrackedRide',
                'Waypoint',
                'Collection',
                'Geofence',
                'Event',
            ].includes(firstItem.entityType)
        ) {
            return throwError(() => new Error(FEATURE_NOT_AVAILABLE));
        } else {
            this.navigateToShareItem(shareId, firstItem);
            return of(true);
        }
    }

    private navigateToRideGroup(inviteCode: string): Observable<boolean> {
        void this.router.navigate(['rides', 'ride-group'], {
            queryParams: { invite: inviteCode },
        });
        return of(true);
    }

    private navigateToShareItem(shareId: string, item: ShareItem): void {
        let itemType = kebabCase(item.entityType);
        if (itemType === 'event') {
            itemType = 'ride-event';
        }

        void this.router.navigate([
            'rides',
            'shared-item',
            shareId,
            itemType,
            item.id,
        ]);
    }

    private navigateToCommunityItem(item: CommunityItem): void {
        let itemType = kebabCase(item.linkedType);
        if (itemType === 'event') {
            itemType = 'ride-event';
        }

        void this.router.navigate([
            'community',
            'activity',
            item.id,
            itemType,
            item.content.contentItem.id,
        ]);
    }
}
